import { Box, Grid } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useForm } from '@ltvco/refresh-lib/vendors';
import { useEffect, type ChangeEvent } from 'react';

import { LicensePlateSearchInput } from '../../../SearchHub/Vehicle/LicensePlateSearchInput';
import {
  type VehicleFormData,
  VehicleSearchType,
  getPlaceholderTextForVehicleSearch,
} from '../../../SearchHub/Vehicle/utils';
import { YMMSearchInput } from '../../../SearchHub/Vehicle/YMMSearchInput';

import {
  RecallsFormContainer,
  RecallsSearchFieldContainer,
  RecallsIntegratedSearchContainer,
  RecallsSearchGridInner,
  RecallsYMMWrapper,
  SearchBarInput,
  SearchButton,
} from './components';
import { RecallsSearchTypeSelect } from './RecallsSearchTypeSelect';
import { buildValuesForPill, defaultFieldConfigs } from './utils';

import type { RecallsFormProps, VehicleFormProps } from './types';

export const RecallsForm = ({
  searchTypeOptions,
  fieldConfigs = defaultFieldConfigs,
  savedDropdown,
  searchValue,
  onSearchValueChange,
  onSearchTypeChange,
  activePill,
  onSubmit,
}: RecallsFormProps) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    register,
    trigger,
    formState: { errors },
  } = useForm<VehicleFormData>({
    defaultValues: buildValuesForPill(
      searchTypeOptions,
      fieldConfigs,
      savedDropdown,
      searchValue
    ),
  });
  const { isMobile } = useScreenSize();

  const searchTypeValue = watch('searchType');
  const yearValue = watch('year');
  const makeValue = watch('make');
  const modelValue = watch('model');
  const trimValue = watch('trim');
  const licensePlateValue = watch('licensePlate');
  const stateValue = watch('state');

  const vehicleFormProps: VehicleFormProps = {
    register,
    errors,
    clearErrors,
    setValue,
    control,
    trigger,
  };

  useEffect(() => {
    const defaultValuesForPill = buildValuesForPill(
      searchTypeOptions,
      fieldConfigs,
      savedDropdown,
      searchValue
    );
    reset(defaultValuesForPill);
  }, [activePill, searchTypeOptions, fieldConfigs, savedDropdown, reset]);

  useEffect(() => {
    setValue('query', searchValue);
  }, [searchValue, setValue]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit({ ...values, query: values.query ?? searchValue });
  });

  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onSearchValueChange(v);
    setValue('query', v);
  };

  const searchPlaceholder = `Search by ${
    searchTypeOptions.find((o) => o.value === searchTypeValue)?.label ?? '...'
  }`;

  const searchTypeSelectProps = {
    options: searchTypeOptions,
    value: searchTypeValue,
    onSearchTypeChange: (value: string) => {
      setValue('searchType', value as VehicleFormData['searchType']);
      onSearchTypeChange?.(value);
    },
    control,
  };

  const renderVehicleSearchInput = () => {
    switch (searchTypeValue) {
      case 'YEAR_MAKE_MODEL':
        return (
          <RecallsYMMWrapper>
            <YMMSearchInput
              {...vehicleFormProps}
              yearValue={yearValue}
              makeValue={makeValue}
              modelValue={modelValue}
              trimValue={trimValue}
              isMobile={isMobile}
            />
          </RecallsYMMWrapper>
        );
      case 'LICENSE_PLATE':
        return (
          <RecallsYMMWrapper>
            <LicensePlateSearchInput
              {...vehicleFormProps}
              placeholderText={getPlaceholderTextForVehicleSearch(
                VehicleSearchType.LICENSE_PLATE,
                isMobile
              )}
              value={licensePlateValue}
              stateValue={stateValue}
              isMobile={isMobile}
            />
          </RecallsYMMWrapper>
        );
      default:
        return isMobile ? (
          <RecallsSearchFieldContainer sx={{ px: 2, py: 1 }}>
            <SearchBarInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchValueChange}
            />
          </RecallsSearchFieldContainer>
        ) : (
          <SearchBarInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={handleSearchValueChange}
          />
        );
    }
  };

  const renderMobileForm = () => (
    <Grid container sx={{ mb: 2.5, mt: 2.5 }} gap={2}>
      <RecallsSearchFieldContainer>
        <RecallsSearchTypeSelect
          {...searchTypeSelectProps}
          wrapperSx={{ width: '100%' }}
        />
      </RecallsSearchFieldContainer>

      <Box flexGrow={1} sx={{ width: '100%' }}>
        {renderVehicleSearchInput()}
      </Box>

      <SearchButton type="submit" fullWidth>
        Search
      </SearchButton>
    </Grid>
  );

  const renderDesktopForm = () => (
    <RecallsFormContainer>
      <RecallsSearchFieldContainer container sx={{ flex: 1, minWidth: 0 }}>
        <RecallsSearchGridInner size={{ xs: 12 }}>
          <RecallsIntegratedSearchContainer>
            <RecallsSearchTypeSelect {...searchTypeSelectProps} />
            <Box
              sx={{
                flex: 1,
                ...(searchTypeValue === 'YEAR_MAKE_MODEL'
                  ? { pr: 1 }
                  : { px: 1 }),
              }}
            >
              {renderVehicleSearchInput()}
            </Box>
          </RecallsIntegratedSearchContainer>
        </RecallsSearchGridInner>
      </RecallsSearchFieldContainer>
      <SearchButton type="submit">Search</SearchButton>
    </RecallsFormContainer>
  );

  return (
    <form onSubmit={handleFormSubmit}>
      {isMobile ? renderMobileForm() : renderDesktopForm()}
    </form>
  );
};
